import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { XCircle, Banknote, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Cancel a booking with an explicit owner decision on refund.
 * - If no token was collected: just confirms cancel (no refund UI).
 * - If token was collected: owner must choose Refund or Forfeit.
 * - Partial refunds supported (amount ≤ token_amount).
 */
const CancelBookingModal = ({ open, onClose, onSuccess, booking, token }) => {
  const tokenPaid = !!booking?.token_paid;
  const tokenAmount = Number(booking?.token_amount || 0);

  const [refund, setRefund] = useState(null); // null | true | false
  const [amount, setAmount] = useState(tokenAmount || '');
  const [method, setMethod] = useState(booking?.token_method || 'upi');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(today());
  const [saving, setSaving] = useState(false);

  const doCancel = async () => {
    if (tokenPaid && refund === null) {
      toast({ title: 'Pick refund decision', description: 'Refund the token or mark as forfeited.', variant: 'destructive' });
      return;
    }
    if (refund && (!amount || parseFloat(amount) <= 0)) {
      toast({ title: 'Enter refund amount', variant: 'destructive' });
      return;
    }
    if (refund && parseFloat(amount) > tokenAmount) {
      toast({ title: 'Refund exceeds token', description: `Max refundable: ₹${tokenAmount}`, variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/bookings/${booking.id}/cancel`, {
        reason: reason || null,
        refund_token: !!refund,
        refund_amount: refund ? parseFloat(amount) : 0,
        refund_method: refund ? method : null,
        refund_date: refund ? date : null,
      }, { headers: { Authorization: `Bearer ${token}` } });

      const outcome = res.data?.refund_outcome;
      toast({
        title: 'Booking cancelled',
        description: outcome === 'full' ? `Full refund of ₹${amount} logged.`
          : outcome === 'partial' ? `Partial refund of ₹${amount} logged.`
          : outcome === 'forfeited' ? `Token of ₹${tokenAmount} marked as forfeited.`
          : 'No token was collected.',
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      toast({
        title: 'Cancel failed',
        description: e.response?.data?.detail || 'Try again',
        variant: 'destructive',
      });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <XCircle size={20} /> Cancel Booking
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Booking summary */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
            <p className="font-semibold text-gray-900">{booking?.name}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {booking?.property_name}
              {booking?.room_number ? ` · Room ${booking.room_number}` : ''}
              {` · Check-in ${booking?.check_in_date}`}
            </p>
            {tokenPaid ? (
              <p className="mt-2 text-sm">
                Token collected: <span className="font-bold text-green-700">₹{tokenAmount.toLocaleString()}</span>
                {booking?.token_method && <span className="text-xs text-gray-500 ml-1">({booking.token_method})</span>}
              </p>
            ) : (
              <p className="mt-2 text-xs text-gray-500 italic">No token was collected on this booking.</p>
            )}
          </div>

          {/* Refund decision — only when token was paid */}
          {tokenPaid && (
            <div>
              <Label>Refund the token?</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  data-testid="cancel-refund-yes"
                  onClick={() => setRefund(true)}
                  className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition ${
                    refund === true
                      ? 'border-green-500 bg-green-50 text-green-800 ring-2 ring-green-200'
                      : 'border-gray-300 hover:border-green-400 hover:bg-green-50 text-gray-700'
                  }`}
                >
                  <Banknote size={16} className="inline mr-1" /> Yes, refund
                </button>
                <button
                  type="button"
                  data-testid="cancel-refund-no"
                  onClick={() => { setRefund(false); setAmount(''); }}
                  className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition ${
                    refund === false
                      ? 'border-red-500 bg-red-50 text-red-800 ring-2 ring-red-200'
                      : 'border-gray-300 hover:border-red-400 hover:bg-red-50 text-gray-700'
                  }`}
                >
                  <AlertTriangle size={16} className="inline mr-1" /> No, forfeit
                </button>
              </div>
            </div>
          )}

          {/* Refund details */}
          {tokenPaid && refund === true && (
            <div className="space-y-3 p-3 border border-green-200 rounded-lg bg-green-50/40" data-testid="cancel-refund-details">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Refund Amount (₹) *</Label>
                  <Input
                    data-testid="cancel-refund-amount"
                    type="number"
                    min="0"
                    max={tokenAmount}
                    step="0.01"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Max ₹{tokenAmount.toLocaleString()} (partial refunds allowed)</p>
                </div>
                <div>
                  <Label>Method</Label>
                  <select
                    data-testid="cancel-refund-method"
                    value={method}
                    onChange={e => setMethod(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="upi">UPI</option>
                    <option value="cash">Cash</option>
                    <option value="bank">Bank Transfer</option>
                    <option value="card">Card</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Refund Date</Label>
                <Input data-testid="cancel-refund-date" type="date" value={date} onChange={e => setDate(e.target.value)} />
              </div>
            </div>
          )}

          {/* Forfeit note */}
          {tokenPaid && refund === false && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800 flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              Token of <span className="font-bold">₹{tokenAmount.toLocaleString()}</span> will be marked as <span className="font-bold">forfeited</span>. Client keeps zero, you retain the full amount.
            </div>
          )}

          {/* Reason */}
          <div>
            <Label>Reason (Optional)</Label>
            <textarea
              data-testid="cancel-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g., Client chose another PG, unable to verify documents..."
            />
          </div>
        </div>

        <div className="flex gap-3 pt-4 border-t">
          <Button
            data-testid="cancel-abort-btn"
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={saving}
            className="flex-1"
          >
            Keep Booking
          </Button>
          <Button
            data-testid="cancel-confirm-btn"
            type="button"
            onClick={doCancel}
            disabled={saving || (tokenPaid && refund === null)}
            className="flex-1 bg-red-600 hover:bg-red-700"
          >
            {saving ? 'Cancelling...' : 'Confirm Cancel'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CancelBookingModal;
